import { useState, useCallback, useRef, useMemo } from "react";
import { TourCtx } from "./useTour";
import { TOUR_STEPS } from "./steps";
import Spotlight from "./Spotlight";
import { markOnboarded } from "../lib/account";
import { track } from "../lib/usage";

const MOBILE_Q = "(max-width: 767px)";

/* Holds the tour's run state and exposes controls via context. Renders the
   Spotlight while active. The cross-component control bus: AppShell and the
   trainer `register()` imperative handles (setTab / openMenu / openDrawer …)
   into `controlsRef`; a step's enter/leave (run by the Spotlight) use them to
   set up the screen before the target is measured. Steps can be platform-
   scoped; `isMobile` is captured at start and filters the step list. Finishing
   or skipping persists `onboarded`. */
export default function TourProvider({ children }) {
  const [active, setActive] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const controlsRef = useRef({});

  const steps = useMemo(
    () =>
      TOUR_STEPS.filter((s) =>
        !s.platform || s.platform === "all"
          ? true
          : s.platform === (isMobile ? "mobile" : "desktop"),
      ),
    [isMobile],
  );
  const lastIndex = steps.length - 1;

  const start = useCallback(() => {
    setIsMobile(window.matchMedia(MOBILE_Q).matches);
    setStepIndex(0);
    setActive(true);
  }, []);

  const seenIt = useCallback(() => {
    setActive(false);
    markOnboarded();
    // Always land the user back on Practice / app, however they exit the tour.
    const c = controlsRef.current;
    c.shell?.gotoApp?.();
    c.shell?.setTab?.("Practice");
  }, []);

  // Plain (not memoized) — they close over the current lastIndex, and nothing
  // depends on their identity in an effect.
  const next = () => setStepIndex((i) => Math.min(i + 1, lastIndex));
  const prev = () => setStepIndex((i) => Math.max(0, i - 1));

  // Both persist `onboarded`; they differ only in the analytics signal.
  const finish = () => {
    track("tour_completed");
    seenIt();
  };
  const skip = () => {
    track("tour_skipped");
    seenIt();
  };

  const register = useCallback((name, handles) => {
    controlsRef.current[name] = handles;
  }, []);

  const value = {
    active,
    stepIndex,
    step: steps[Math.min(stepIndex, Math.max(0, lastIndex))],
    steps,
    isFirst: stepIndex === 0,
    isLast: stepIndex === lastIndex,
    isMobile,
    controls: controlsRef, // ref object; read .current in effects
    start,
    next,
    prev,
    finish,
    skip,
    register,
  };

  return (
    <TourCtx.Provider value={value}>
      {children}
      {active && <Spotlight />}
    </TourCtx.Provider>
  );
}
